import { useState, useEffect, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { FileText, Trash2, Clock, ChevronRight, Cloud, HardDrive, Loader2, CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useDrafts, type Draft } from '@/hooks/useDrafts';
import { usePublishedArticles, type PublishedArticle } from '@/hooks/usePublishedArticles';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { toast } from '@/hooks/useToast';

interface DraftsPanelProps {
  onSelectDraft: (draft: Omit<Draft, 'id' | 'updatedAt' | 'eventId'>) => void;
  onSelectArticle?: (article: PublishedArticle) => void;
  trigger?: React.ReactNode;
}

interface LocalDraft extends Draft {
  isLocal: true;
}

interface RelayDraft extends Draft {
  isLocal: false;
}

type CombinedDraft = LocalDraft | RelayDraft;

const DRAFTS_KEY = 'inkwell-drafts';

export function DraftsPanel({ onSelectDraft, onSelectArticle, trigger }: DraftsPanelProps) {
  const { user } = useCurrentUser();
  const { drafts: relayDrafts, isLoading, deleteDraft, isDeleting } = useDrafts();
  const { articles: publishedArticles, isLoading: isLoadingArticles } = usePublishedArticles();
  const [localDrafts, setLocalDrafts] = useState<Draft[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; slug: string; isLocal: boolean } | null>(null);
  const [activeTab, setActiveTab] = useState<'drafts' | 'published'>('drafts');

  const saveLocalDrafts = useCallback((newDrafts: Draft[]) => {
    setLocalDrafts(newDrafts);
    localStorage.setItem(DRAFTS_KEY, JSON.stringify(newDrafts));
  }, []);

  const loadLocalDrafts = useCallback(() => {
    try {
      const stored = localStorage.getItem(DRAFTS_KEY);
      if (stored) {
        setLocalDrafts(JSON.parse(stored));
      }

      // Also check for legacy single draft
      const legacyDraft = localStorage.getItem('inkwell-draft');
      if (legacyDraft && !stored) {
        const draft = JSON.parse(legacyDraft);
        const newDraft: Draft = {
          ...draft,
          id: crypto.randomUUID(),
          updatedAt: Date.now(),
        };
        saveLocalDrafts([newDraft]);
        localStorage.removeItem('inkwell-draft');
      }
    } catch (error) {
      console.error('Failed to load drafts:', error);
    }
  }, [saveLocalDrafts]);

  useEffect(() => {
    loadLocalDrafts();
  }, [isOpen, loadLocalDrafts]);

  // Combine relay and local drafts, avoiding duplicates by slug
  const combinedDrafts: CombinedDraft[] = (() => {
    const drafts: CombinedDraft[] = [];
    const seenSlugs = new Set<string>();

    // Add relay drafts first (they take priority)
    for (const draft of relayDrafts) {
      if (draft.slug) {
        seenSlugs.add(draft.slug);
      }
      drafts.push({ ...draft, isLocal: false });
    }

    // Add local drafts that don't have a relay version
    for (const draft of localDrafts) {
      if (!draft.slug || !seenSlugs.has(draft.slug)) {
        drafts.push({ ...draft, isLocal: true });
      }
    }

    return drafts.sort((a, b) => b.updatedAt - a.updatedAt);
  })();

  const handleSelect = (draft: CombinedDraft) => {
    onSelectDraft({
      title: draft.title,
      summary: draft.summary,
      content: draft.content,
      image: draft.image,
      tags: draft.tags,
      slug: draft.slug,
    });
    setIsOpen(false);
  };

  const handleSelectArticle = (article: PublishedArticle) => {
    if (onSelectArticle) {
      onSelectArticle(article);
    } else {
      // Fallback to onSelectDraft if onSelectArticle is not provided
      onSelectDraft({
        title: article.title,
        summary: article.summary,
        content: article.content,
        image: article.image,
        tags: article.tags,
        slug: article.slug,
      });
    }
    setIsOpen(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    if (deleteTarget.isLocal) {
      const newDrafts = localDrafts.filter(d => d.id !== deleteTarget.id);
      saveLocalDrafts(newDrafts);
      toast({
        title: 'Draft deleted',
        description: 'The draft has been removed from your browser.',
      });
    } else {
      try {
        await deleteDraft(deleteTarget.slug);
        toast({
          title: 'Draft deleted',
          description: 'The draft deletion event has been published to relays.',
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        const isPermissionError = message.toLowerCase().includes('permission');
        toast({
          title: 'Delete failed',
          description: isPermissionError
            ? 'Your signer denied permission to sign deletion events (kind 5). Check your bunker/extension permissions.'
            : message || 'Could not delete draft from relays. Please try again.',
          variant: 'destructive',
        });
      }
    }
    setDeleteTarget(null);
  };

  const totalDrafts = combinedDrafts.length;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          {trigger || (
            <Button variant="ghost" size="sm" className="gap-2">
              <FileText className="w-4 h-4" />
              Drafts
              {totalDrafts > 0 && (
                <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                  {totalDrafts}
                </span>
              )}
            </Button>
          )}
        </SheetTrigger>
        <SheetContent className="w-full sm:max-w-md flex flex-col">
          <SheetHeader className="mb-4 flex-shrink-0">
            <SheetTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Your Content
            </SheetTitle>
            <SheetDescription>
              {user
                ? 'Manage your drafts and published articles.'
                : 'Sign in to sync content across devices.'}
            </SheetDescription>
          </SheetHeader>

          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'drafts' | 'published')} className="flex flex-col flex-1 min-h-0">
            <TabsList className="grid w-full grid-cols-2 mb-4 flex-shrink-0">
              <TabsTrigger value="drafts" className="gap-2">
                Drafts
                {totalDrafts > 0 && (
                  <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                    {totalDrafts}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="published" className="gap-2">
                Published
                {publishedArticles.length > 0 && (
                  <span className="text-xs bg-green-500/10 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded-full">
                    {publishedArticles.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="drafts" className="mt-0 flex-1 overflow-y-auto">
              {isLoading && user ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Loading drafts...</p>
                </div>
              ) : totalDrafts === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                    <FileText className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">No drafts yet</p>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    Your saved drafts will appear here
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {combinedDrafts.map(draft => (
                    <div
                      key={draft.id}
                      className={cn(
                        'group relative p-4 rounded-xl border border-border',
                        'hover:border-primary/30 hover:bg-card transition-all cursor-pointer'
                      )}
                      onClick={() => handleSelect(draft)}
                    >
                      <div className="pr-8">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium truncate flex-1">
                            {draft.title || 'Untitled Draft'}
                          </h3>
                          {draft.isLocal ? (
                            <HardDrive className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          ) : (
                            <Cloud className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                          )}
                        </div>
                        {draft.summary && (
                          <p className="text-sm text-muted-foreground truncate mt-1">
                            {draft.summary}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>
                            {formatDistanceToNow(draft.updatedAt, { addSuffix: true })}
                          </span>
                          {draft.tags.length > 0 && (
                            <>
                              <span>·</span>
                              <span>{draft.tags.length} tags</span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget({ id: draft.id, slug: draft.slug, isLocal: draft.isLocal });
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="published" className="mt-0 flex-1 overflow-y-auto">
              {isLoadingArticles && user ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Loading articles...</p>
                </div>
              ) : !user ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">Sign in to see your articles</p>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    Your published articles will appear here
                  </p>
                </div>
              ) : publishedArticles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">No published articles yet</p>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    Your published articles will appear here
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {publishedArticles.map(article => (
                    <div
                      key={article.id}
                      className={cn(
                        'group relative p-4 rounded-xl border border-border',
                        'hover:border-green-500/30 hover:bg-card transition-all cursor-pointer'
                      )}
                      onClick={() => handleSelectArticle(article)}
                    >
                      <div className="pr-8">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium truncate flex-1">
                            {article.title || 'Untitled Article'}
                          </h3>
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                        </div>
                        {article.summary && (
                          <p className="text-sm text-muted-foreground truncate mt-1">
                            {article.summary}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>
                            Published {formatDistanceToNow(article.publishedAt, { addSuffix: true })}
                          </span>
                          {article.tags.length > 0 && (
                            <>
                              <span>·</span>
                              <span>{article.tags.length} tags</span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete draft?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.isLocal
                ? 'This action cannot be undone. The draft will be permanently deleted from your browser.'
                : 'This action cannot be undone. The draft will be deleted from Nostr relays.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
